import { Content, getXcoreConfig } from "@robe/core";
import { GetServerSidePropsContext, GetServerSidePropsResult, GetStaticPropsContext, GetStaticPropsResult } from "next";
import { createXcoreContext } from "./createXcoreContext";
import { mergeServerProps, mergeStaticProps } from "./mergeProps";
import { resolveOptions } from "./resolveOptions";
import {
  GetXcoreServerContext,
  GetXcoreServerLayout,
  GetXcoreStaticContext,
  GetXcoreStaticLayout,
  XcorePropsOptions,
  XcoreProps,
  Awaitable,
} from "./types";

interface GetXcorePropsWithLayout<Layout> {
  <PageContent extends Content, ContentSingletons extends Content[] = Content[], MultipleContents extends Content[] = Content[], Data = {}>(
    getOptions: (xcoreCtx: GetXcoreStaticContext) => Awaitable<XcorePropsOptions<PageContent, ContentSingletons, MultipleContents, Data>>
  ): (
    ctx: GetStaticPropsContext
  ) => Promise<GetStaticPropsResult<XcoreProps<PageContent, ContentSingletons, MultipleContents, Data> & Layout>>;
}

/** Wrapper for next.js getStaticProps */
export const getXcoreProps =
  <TLayout>(layoutData?: GetXcoreStaticLayout<TLayout>): GetXcorePropsWithLayout<TLayout> =>
    (getOptions) => {
    /** Classic next.js `GetStaticPropsContext` */
      return async (ctx: GetStaticPropsContext) => {
        const config = await getXcoreConfig();
        /** `core` instance is initialized here */
        const xcoreCtx = await createXcoreContext(ctx);
        const options = await getOptions(xcoreCtx);

        return await mergeStaticProps(config, xcoreCtx, layoutData, resolveOptions(config, options));
      };
    };

interface GetXcoreServerPropsWithLayout<Layout> {
  <PageContent extends Content, ContentSingletons extends Content[] = Content[], MultipleContents extends Content[] = Content[], Data = {}>(
    getOptions: (xcoreCtx: GetXcoreServerContext) => Awaitable<XcorePropsOptions<PageContent, ContentSingletons, MultipleContents, Data>>
  ): (
    ctx: GetServerSidePropsContext
  ) => Promise<GetServerSidePropsResult<XcoreProps<PageContent, ContentSingletons, MultipleContents, Data> & Layout>>;
}

/** Wrapper for next.js getServerSideProps */
export const getXcoreServerProps = <TLayout>(layoutData?: GetXcoreServerLayout<TLayout>): GetXcoreServerPropsWithLayout<TLayout> => {
  return (getOptions) => {
    /** Classic next.js `GetServerSidePropsContext` */
    return async (ctx: GetServerSidePropsContext) => {
      const config = await getXcoreConfig();
      /** `core` instance is initialized here */
      const xcoreCtx = await createXcoreContext(ctx);
      const options = await getOptions(xcoreCtx);

      return await mergeServerProps(xcoreCtx, layoutData, resolveOptions(config, options));
    };
  };
};
